import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import PageTitle from "../../../layouts/PageTitle";
import {
  appApi,
  useGetMyCalendarQuery,
  useGetDailyRosterQuery,
  useGetMyProjectsQuery,
  useGetSelfieConfigQuery,
} from "../../../../store/appApi";
import { punchInApi, punchOutApi, adminPunchOutApi } from "../../../../services/attendance";
import { selectUserRole } from "../../../../store/authSlice";
import { selectActiveProjectId } from "../../../../store/projectSlice";

const GEO_UNKNOWN = "UNKNOWN";
const GEO_DENIED = "DENIED";
const GEO_GRANTED = "GRANTED";

const todayMonthStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const todayDateStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// SUPER-ADMIN — Force punch-out for any employee, any time (no GPS/selfie/time gate)
// ─────────────────────────────────────────────────────────────────────────────
const AdminForcePunchOut = () => {
  const dispatch = useDispatch();
  const [date, setDate] = useState(todayDateStr());
  const [submittingId, setSubmittingId] = useState(null);

  const { data, isLoading, isFetching, refetch } = useGetDailyRosterQuery({ date });
  const roster = data?.roster || [];

  const forcePunchOut = async (employeeId, name) => {
    // Punch-out time (India time) on the punch-in day — defaults to 6:30 PM.
    const confirm = await Swal.fire({
      icon: "warning",
      title: `Force Punch Out ${name}?`,
      html:
        "This overrides the employee's attendance regardless of location." +
        '<div style="margin-top:14px;font-size:13px;color:#6b7280">Punch-out time</div>',
      input: "time",
      inputValue: "18:30",
      inputAttributes: { required: "true", step: "60" },
      inputValidator: (v) => (!/^\d{2}:\d{2}$/.test(v || "") ? "Pick a punch-out time." : undefined),
      showCancelButton: true,
      confirmButtonText: "Punch Out",
    });
    if (!confirm.isConfirmed) return;
    const time = confirm.value;

    setSubmittingId(employeeId);
    try {
      await adminPunchOutApi({ employeeId, date, time });
      dispatch(appApi.util.invalidateTags(["AttendanceRoster", "AttendanceCalendar"]));
      refetch();
      Swal.fire("Success", `${name} has been punched out at ${time}.`, "success");
    } catch (err) {
      Swal.fire("Failed", err?.response?.data?.message || "Could not punch out employee.", "error");
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4 className="card-title mb-0">Super Admin — Force Punch Out</h4>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: 160 }}
          max={todayDateStr()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>
      <div className="card-body">
        {(isLoading || isFetching) ? (
          <div className="text-center py-3">Loading…</div>
        ) : roster.length === 0 ? (
          <div className="text-center text-muted py-3">No employees found for this date.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Punch In</th>
                  <th>Punch Out</th>
                  <th style={{ width: 160 }}></th>
                </tr>
              </thead>
              <tbody>
                {roster.map((emp) => {
                  const empId = emp.employeeId || emp.userId;
                  // Only an employee who punched in but never punched out is
                  // eligible for admin override — they can't be closed out
                  // before they've actually punched in themselves.
                  const eligible = !!emp.punchIn && !emp.punchOut;
                  return (
                    <tr key={empId}>
                      <td>{emp.name}</td>
                      <td>{emp.role}</td>
                      <td>{emp.punchIn ? new Date(emp.punchIn).toLocaleTimeString() : "—"}</td>
                      <td>{emp.punchOut ? new Date(emp.punchOut).toLocaleTimeString() : "—"}</td>
                      <td>
                        {eligible ? (
                          <button
                            className="btn btn-xs btn-danger"
                            disabled={submittingId === empId}
                            onClick={() => forcePunchOut(empId, emp.name)}
                          >
                            {submittingId === empId ? "…" : "Force Punch Out"}
                          </button>
                        ) : emp.punchOut ? (
                          <span className="text-muted small">Completed</span>
                        ) : (
                          <span className="text-muted small">Not punched in</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Punch = () => {
  const dispatch = useDispatch();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";
  const activeProjectId = useSelector(selectActiveProjectId);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [geoState, setGeoState] = useState(GEO_UNKNOWN);
  const [coords, setCoords] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Every project this employee is assigned to — they can punch in/out for
  // any of them, not just whichever project happens to be globally active.
  const { data: myProjectsData } = useGetMyProjectsQuery();
  const myProjects = myProjectsData?.projects || [];

  useEffect(() => {
    if (selectedProjectId || myProjects.length === 0) return;
    const activeIsAssigned = myProjects.some(
      (project) => (project._id || project.id) === activeProjectId,
    );
    setSelectedProjectId(
      activeIsAssigned ? activeProjectId : myProjects[0]._id || myProjects[0].id,
    );
  }, [myProjects, activeProjectId, selectedProjectId]);

  const { data: selfieConfigData } = useGetSelfieConfigQuery();
  const selfieRequired = selfieConfigData?.selfieRequired ?? true;

  // Fetch today's calendar to know punch state
  const { data: calData, refetch } = useGetMyCalendarQuery(todayMonthStr());
  const todayRecord = calData?.days?.find((d) => d.date === todayDateStr());
  const alreadyPunchedIn = !!todayRecord?.punchIn;
  const alreadyPunchedOut = !!todayRecord?.punchOut;

  const requestGps = () => {
    setError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoState(GEO_GRANTED);
      },
      () => {
        setGeoState(GEO_DENIED);
        setError("Location access denied. Enable GPS and try again.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen]);

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Camera API not available. Make sure the page is served over HTTPS.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Camera permission denied. Allow camera access in your browser settings and try again.");
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else if (err.name === "NotReadableError") {
        setError("Camera is in use by another application.");
      } else {
        setError(`Camera error: ${err.message || err.name}`);
      }
    }
  };

  const captureSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      setSelfieBlob(blob);
      setSelfiePreview(canvas.toDataURL("image/jpeg"));
    }, "image/jpeg", 0.8);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setCameraOpen(false);
  };

  const punch = async (type) => {
    if (!coords) return;
    setError("");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("lat", coords.lat);
      fd.append("lng", coords.lng);
      if (selfieBlob) fd.append("selfie", selfieBlob, "selfie.jpg");

      const apiFn = type === "in" ? punchInApi : punchOutApi;
      const res = await apiFn(fd, selectedProjectId || undefined);
      dispatch(appApi.util.invalidateTags(["AttendanceCalendar"]));
      refetch();
      // Punches outside the configured work zone are no longer rejected —
      // they're recorded and flagged for admin review, so let the employee
      // know that's what happened instead of a silent identical "Success".
      if (res?.data?.withinGeofence === false) {
        Swal.fire(
          "Recorded — outside work zone",
          `Punch ${type === "in" ? "In" : "Out"} was recorded, but you were outside the designated work location. This has been flagged for admin review.`,
          "warning",
        );
      } else {
        Swal.fire("Success", `Punch ${type === "in" ? "In" : "Out"} recorded!`, "success");
      }
      setSelfieBlob(null);
      setSelfiePreview(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Punch failed");
    } finally {
      setLoading(false);
    }
  };

  const canPunch = selfieRequired ? !!selfieBlob : true;

  return (
    <>
      <PageTitle activeMenu="Punch In / Out" motherMenu="Attendance" />
      <div className="row">
        <div className="col-xl-6 col-lg-8 mx-auto">

          {/* Today's status banner */}
          {todayRecord && (
            <div className={`alert py-2 mb-3 ${alreadyPunchedOut ? "alert-success" : alreadyPunchedIn ? "alert-warning" : "alert-secondary"}`}>
              {alreadyPunchedOut ? (
                <>Punched out at <strong>{new Date(todayRecord.punchOut).toLocaleTimeString()}</strong>. Work hours: <strong>{((todayRecord.workDurationMinutes || 0) / 60).toFixed(1)}h</strong></>
              ) : alreadyPunchedIn ? (
                <>Punched in at <strong>{new Date(todayRecord.punchIn).toLocaleTimeString()}</strong> — remember to punch out.</>
              ) : todayRecord.tag === "HOLIDAY" ? (
                <>Today is a holiday{todayRecord.holiday ? <> — <strong>{todayRecord.holiday}</strong></> : null}. No punch needed.</>
              ) : (
                "Not punched in yet today."
              )}
              {todayRecord.tag === "HALF_DAY_LEAVE" && (
                <span className="badge ms-2" style={{ backgroundColor: "#14b8a6", color: "#fff" }}>
                  Half Day
                </span>
              )}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Attendance Punch</h4>
            </div>
            <div className="card-body">
              {error && <div className="alert alert-danger">{error}</div>}

              {/* Project picker — punch in/out at any assigned project */}
              {myProjects.length > 0 && (
                <div className="mb-4">
                  <label className="form-label">Project</label>
                  <select
                    className="form-control"
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    disabled={alreadyPunchedIn && !alreadyPunchedOut}
                  >
                    {myProjects.map((project) => (
                      <option key={project._id || project.id} value={project._id || project.id}>
                        {project.projectName}
                        {project.city ? ` — ${project.city}` : ""}
                      </option>
                    ))}
                  </select>
                  {alreadyPunchedIn && !alreadyPunchedOut && (
                    <div className="form-text">
                      You're already punched in today — punch out before switching projects.
                    </div>
                  )}
                </div>
              )}

              {/* Step 1: GPS */}
              <div className="mb-4">
                <h6 className="mb-2">Step 1 — Verify Location</h6>
                {geoState === GEO_UNKNOWN && (
                  <button className="btn btn-primary" onClick={requestGps}>
                    <i className="fas fa-map-marker-alt me-2" />
                    Request GPS
                  </button>
                )}
                {geoState === GEO_DENIED && (
                  <button className="btn btn-warning" onClick={requestGps}>Retry GPS</button>
                )}
                {geoState === GEO_GRANTED && coords && (
                  <div className="alert alert-success py-2">
                    <i className="fas fa-check-circle me-2" />
                    Location acquired ({coords.lat.toFixed(5)}, {coords.lng.toFixed(5)})
                  </div>
                )}
              </div>

              {/* Step 2: Selfie (skipped entirely when disabled by admin) */}
              {geoState === GEO_GRANTED && selfieRequired && (
                <div className="mb-4">
                  <h6 className="mb-2">Step 2 — Selfie (Required)</h6>
                  {!cameraOpen && !selfiePreview && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={openCamera}
                      disabled={alreadyPunchedIn && alreadyPunchedOut}
                      title={
                        alreadyPunchedIn && alreadyPunchedOut
                          ? "You've already punched in and out for today"
                          : ""
                      }
                    >
                      <i className="fas fa-camera me-2" />Open Camera
                    </button>
                  )}
                  {cameraOpen && (
                    <div>
                      <video ref={videoRef} className="w-100 rounded mb-2" />
                      <button className="btn btn-success w-100" onClick={captureSelfie}>Capture</button>
                    </div>
                  )}
                  {selfiePreview && (
                    <div className="text-center">
                      <img src={selfiePreview} alt="selfie" className="rounded mb-2" style={{ maxHeight: 200 }} />
                      <br />
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setSelfieBlob(null); setSelfiePreview(null); }}
                      >
                        Retake
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Punch buttons */}
              {geoState === GEO_GRANTED && (
                <div className="d-flex gap-3 mt-3">
                  <button
                    className="btn btn-success flex-fill"
                    disabled={loading || alreadyPunchedIn || !canPunch}
                    title={alreadyPunchedIn ? "Already punched in today" : !canPunch ? "Take a selfie first" : ""}
                    onClick={() => punch("in")}
                  >
                    {loading ? "..." : alreadyPunchedIn ? "Punched In ✓" : "Punch In"}
                  </button>
                  <button
                    className="btn btn-danger flex-fill"
                    disabled={loading || !alreadyPunchedIn || alreadyPunchedOut || !canPunch}
                    title={!alreadyPunchedIn ? "Punch in first" : alreadyPunchedOut ? "Already punched out today" : !canPunch ? "Take a selfie first" : ""}
                    onClick={() => punch("out")}
                  >
                    {loading ? "..." : alreadyPunchedOut ? "Punched Out ✓" : "Punch Out"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="row">
          <div className="col-12">
            <AdminForcePunchOut />
          </div>
        </div>
      )}
      <canvas ref={canvasRef} className="d-none" />
    </>
  );
};

export default Punch;
